import { useFormik } from 'formik';
import {
  ErrorMessage,
  ADD_FORM,
  removeFollowingFields,
} from '@employ-engage/common-components';
import { FormType } from '@employ-engage/data';
import * as Yup from 'yup';
import { useState } from 'react';
import { addWeeks, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

export interface DuplicateFormFormProps {
  onClose: () => void;
  formSelectedForDuplication: FormType;
}

const toInputValue = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm:ss");

export function DuplicateFormForm({
  onClose,
  formSelectedForDuplication,
}: DuplicateFormFormProps) {
  const { t } = useTranslation(['common']);
  const [addForm] = useMutation(ADD_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const now = new Date();

  const { handleSubmit, values, errors, touched, handleChange, handleBlur } =
    useFormik({
      initialValues: {
        title: formSelectedForDuplication?.title + ' (copy)',
        // A copy is a new run of the form, so it starts from today rather than
        // inheriting the original's window — which used to carry the same
        // dates from copy to copy indefinitely.
        openDate: toInputValue(now),
        closeDate: toInputValue(addWeeks(now, 2)),
        form: formSelectedForDuplication,
      },
      onSubmit: async (values, { resetForm }) => {
        setSubmitError(null);
        const newForm: { id?: string; title: string; [key: string]: any } =
          removeFollowingFields(
            {
              ...values.form,
              title: values.title,
              openDate: new Date(values.openDate).toISOString(),
              closeDate: new Date(values.closeDate).toISOString(),
            },
            ['id', 'createdAt', 'updatedAt', '__typename', 'companyId']
          );

        try {
          await addForm({ variables: { input: newForm } });
        } catch (error: any) {
          setSubmitError(error?.message || t("Couldn't duplicate the form."));
          return;
        }
        resetForm();
        onClose();
      },
      validationSchema: Yup.object().shape({
        title: Yup.string().required(t('Required')),
        openDate: Yup.string().required(t('Required')),
        closeDate: Yup.string()
          .required(t('Required'))
          .test(
            'after-open',
            t('Close date must be after the open date.'),
            function (closeDate) {
              const { openDate } = this.parent;
              if (!closeDate || !openDate) return true;
              return new Date(closeDate) > new Date(openDate);
            }
          ),
      }),
    });

  return (
    <form className="mt-10 w-full" onSubmit={handleSubmit}>
      <label
        htmlFor="title"
        className="block text-sm font-medium leading-5 text-gray-700"
      >
        {t('New Form Title')}
      </label>
      <div className="mt-1 rounded-md shadow-sm">
        <input
          id="title"
          name="title"
          type="text"
          value={values.title}
          onChange={handleChange}
          className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
        />
        <ErrorMessage errors={errors.title} touched={touched.title} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="openDate"
            className="block text-sm font-medium leading-5 text-gray-700"
          >
            {t('Open Date')}
          </label>
          <input
            id="openDate"
            name="openDate"
            type="datetime-local"
            value={values.openDate}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-input mt-1 block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
          <ErrorMessage errors={errors.openDate} touched={touched.openDate} />
        </div>
        <div>
          <label
            htmlFor="closeDate"
            className="block text-sm font-medium leading-5 text-gray-700"
          >
            {t('Close Date')}
          </label>
          <input
            id="closeDate"
            name="closeDate"
            type="datetime-local"
            value={values.closeDate}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-input mt-1 block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
          <ErrorMessage errors={errors.closeDate} touched={touched.closeDate} />
        </div>
      </div>

      {submitError && (
        <p className="mt-4 text-sm text-red-500" role="alert">
          {submitError}
        </p>
      )}

      <div className="mt-6">
        <button
          type="submit"
          className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-light-primary hover:bg-light-primaryAccent focus:outline-none focus:border-light-primaryAccent focus:shadow-outline-indigo active:bg-light-primaryAccent transition duration-150 ease-in-out"
        >
          {t('Duplicate Form')}
        </button>
      </div>
    </form>
  );
}

export default DuplicateFormForm;
